import {
  Group,
  InstancedBufferAttribute,
  InstancedMesh,
  Line,
  LineLoop,
  LineSegments,
  type Material,
  Matrix4,
  Mesh,
  type Object3D,
  Points,
  SkinnedMesh
} from "three";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";
import { clipMeshGeometry } from "./clipped-mesh-geometry";
import { mergeRackExportGeometry } from "./merged-rack-export-geometry";

const excludedNodeNames = new Set([
  "bin-rack-cargo",
  "bin-rack-cargo-cells",
  "pallet-rack-cargo-cells",
  "rack-shelf-cargo-cell-hit-targets",
  "rack-shelf-cargo-cell-highlight",
  "rack-shelf-cargo-cell-selection-highlight",
  "rack-shelf-selection-highlight",
  "device-selection-highlight",
  "pallet-stacker-crane-hit-target",
  "pallet-stacker-crane-selection-highlight",
  "pallet-reciprocating-lift-hit-target",
  "pallet-reciprocating-lift-selection-highlight"
]);
const exportedUserDataKeys = ["cadDeviceId", "deviceModelName", "dxfRackId"] as const;
const instanceDeterminantEpsilon = 1e-10;
const trailingNumericSuffixPattern = /\.(\d+)$/;

type ExportCopyContext = {
  instancedMeshes: InstancedMesh[];
  materials: Material[];
  geometries: ReturnType<typeof clipMeshGeometry>[];
};

export type CargoSceneExportCopy = {
  dispose: () => void;
  root: Group;
};

export async function exportCargoSceneGlb(contentRoot: Object3D) {
  const copy = createCargoSceneExportCopy(contentRoot);
  try {
    if (!hasRenderableContent(copy.root)) {
      throw new Error("当前场景没有可导出的三维模型");
    }

    const result = await new GLTFExporter().parseAsync(copy.root, {
      binary: true,
      onlyVisible: true
    });
    if (!(result instanceof ArrayBuffer)) {
      throw new TypeError("GLB 导出器返回了非二进制结果");
    }
    assertBlenderSafeNodeNames(result);
    return result;
  } finally {
    copy.dispose();
  }
}

export function createCargoSceneExportCopy(contentRoot: Object3D): CargoSceneExportCopy {
  contentRoot.updateWorldMatrix(true, true);
  const context: ExportCopyContext = { geometries: [], instancedMeshes: [], materials: [] };
  let copiedRoot: Object3D | undefined;
  try {
    copiedRoot = copyExportNode(contentRoot, context);
    if (!(copiedRoot instanceof Group)) {
      throw new TypeError("场景导出根节点必须是 Group");
    }
    context.geometries.push(...mergeRackExportGeometry(copiedRoot));
  } catch (error) {
    disposeExportCopyContext(context);
    throw error;
  }

  let disposed = false;
  return {
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      disposeExportCopyContext(context);
    },
    root: copiedRoot
  };
}

function copyExportNode(source: Object3D, context: ExportCopyContext): Object3D | undefined {
  if (!source.visible || excludedNodeNames.has(source.name)) {
    return;
  }

  const target = createExportNode(source, context);
  if (!target) {
    return;
  }
  copyObjectProperties(source, target);
  source.children.forEach((child) => {
    const copiedChild = copyExportNode(child, context);
    if (copiedChild) {
      target.add(copiedChild);
    }
  });
  return target;
}

function createExportNode(source: Object3D, context: ExportCopyContext): Object3D | undefined {
  if (source instanceof InstancedMesh) {
    return copyInstancedMesh(source, context);
  }
  if (source instanceof Mesh) {
    return copyMesh(source, context);
  }
  if (source instanceof LineSegments) {
    return new LineSegments(source.geometry, source.material);
  }
  if (source instanceof LineLoop) {
    return new LineLoop(source.geometry, source.material);
  }
  if (source instanceof Line) {
    return new Line(source.geometry, source.material);
  }
  if (source instanceof Points) {
    return new Points(source.geometry, source.material);
  }
  return new Group();
}

function copyMesh(source: Mesh, context: ExportCopyContext) {
  if (source instanceof SkinnedMesh) {
    throw new Error(`暂不支持导出蒙皮模型：${source.name || "未命名模型"}`);
  }

  const clippingPlanes = getSharedClippingPlanes(source.material);
  if (clippingPlanes.length === 0) {
    return new Mesh(source.geometry, source.material);
  }

  const geometry = clipMeshGeometry(source.geometry, clippingPlanes, source.matrixWorld);
  const material = cloneMaterialWithoutClipping(source.material);
  context.geometries.push(geometry);
  context.materials.push(...toMaterialArray(material));
  return new Mesh(geometry, material);
}

function copyInstancedMesh(source: InstancedMesh, context: ExportCopyContext) {
  if (getSharedClippingPlanes(source.material).length > 0) {
    throw new Error(`暂不支持裁切实例化模型：${source.name || "未命名模型"}`);
  }

  const sourceMatrix = new Matrix4();
  const activeIndices: number[] = [];
  for (let index = 0; index < source.count; index += 1) {
    source.getMatrixAt(index, sourceMatrix);
    if (Math.abs(sourceMatrix.determinant()) > instanceDeterminantEpsilon) {
      activeIndices.push(index);
    }
  }

  if (activeIndices.length === 0) {
    return;
  }

  const target = new InstancedMesh(source.geometry, source.material, activeIndices.length);
  activeIndices.forEach((sourceIndex, targetIndex) => {
    source.getMatrixAt(sourceIndex, sourceMatrix);
    target.setMatrixAt(targetIndex, sourceMatrix);
  });
  target.instanceMatrix.setUsage(source.instanceMatrix.usage);

  const sourceInstanceColor = source.instanceColor;
  if (sourceInstanceColor) {
    const colorValues = new Float32Array(activeIndices.length * sourceInstanceColor.itemSize);
    activeIndices.forEach((sourceIndex, targetIndex) => {
      for (let offset = 0; offset < sourceInstanceColor.itemSize; offset += 1) {
        colorValues[targetIndex * sourceInstanceColor.itemSize + offset] =
          sourceInstanceColor.array[sourceIndex * sourceInstanceColor.itemSize + offset];
      }
    });
    target.instanceColor = new InstancedBufferAttribute(
      colorValues,
      sourceInstanceColor.itemSize,
      sourceInstanceColor.normalized
    );
  }

  context.instancedMeshes.push(target);
  return target;
}

function copyObjectProperties(source: Object3D, target: Object3D) {
  target.name = createExportNodeName(source.name);
  target.up.copy(source.up);
  target.position.copy(source.position);
  target.quaternion.copy(source.quaternion);
  target.scale.copy(source.scale);
  target.matrix.copy(source.matrix);
  target.matrixAutoUpdate = source.matrixAutoUpdate;
  target.matrixWorldAutoUpdate = source.matrixWorldAutoUpdate;
  target.layers.mask = source.layers.mask;
  target.castShadow = source.castShadow;
  target.receiveShadow = source.receiveShadow;
  target.frustumCulled = source.frustumCulled;
  target.renderOrder = source.renderOrder;
  target.userData = Object.fromEntries(
    exportedUserDataKeys.flatMap((key) =>
      source.userData[key] === undefined ? [] : [[key, source.userData[key]]]
    )
  );
}

function createExportNodeName(name: string) {
  // Blender parses trailing .<digits> as a signed 32-bit duplicate suffix.
  return name.replace(trailingNumericSuffixPattern, "_$1");
}

function assertBlenderSafeNodeNames(glb: ArrayBuffer) {
  const jsonChunkLength = new DataView(glb).getUint32(12, true);
  const json = JSON.parse(
    new TextDecoder().decode(new Uint8Array(glb, 20, jsonChunkLength)).trimEnd()
  ) as { nodes?: Array<{ name?: string }> };
  const incompatibleNode = json.nodes?.find(
    (node) => node.name && trailingNumericSuffixPattern.test(node.name)
  );
  if (incompatibleNode?.name) {
    throw new Error(`GLB 节点名与 Blender 不兼容：${incompatibleNode.name}`);
  }
}

function getSharedClippingPlanes(material: Material | Material[]) {
  const materials = toMaterialArray(material);
  const clippingPlanes =
    materials.find((item) => item.clippingPlanes?.length)?.clippingPlanes ?? [];
  if (clippingPlanes.length === 0) {
    return [];
  }
  if (
    materials.some(
      (item) =>
        item.clipIntersection ||
        item.clippingPlanes?.length !== clippingPlanes.length ||
        item.clippingPlanes.some((plane, index) => !plane.equals(clippingPlanes[index]))
    )
  ) {
    throw new Error("同一个模型的材质使用了不一致的裁切区域");
  }
  return clippingPlanes;
}

function cloneMaterialWithoutClipping(material: Material | Material[]): Material | Material[] {
  if (Array.isArray(material)) {
    return material.map((item) => cloneMaterialWithoutClipping(item) as Material);
  }
  const cloned = material.clone();
  cloned.clippingPlanes = null;
  cloned.clipIntersection = false;
  return cloned;
}

function toMaterialArray(material: Material | Material[]) {
  return Array.isArray(material) ? material : [material];
}

function hasRenderableContent(root: Object3D) {
  let hasContent = false;
  root.traverse((object) => {
    if (object instanceof Mesh || object instanceof Line || object instanceof Points) {
      hasContent = true;
    }
  });
  return hasContent;
}

function disposeExportCopyContext(context: ExportCopyContext) {
  context.instancedMeshes.forEach((mesh) => {
    mesh.dispose();
  });
  context.geometries.forEach((geometry) => {
    geometry.dispose();
  });
  context.materials.forEach((material) => {
    material.dispose();
  });
}
